import React, { useEffect, useState } from 'react'

import { RadioGroup, RadioItem, Select } from '@driftt/dds'

import useCalendarConnectedUsers from 'hooks/useCalendarConnectedUsers'
import useCurrentUser from 'hooks/useCurrentUser'
import { User } from 'hooks/useOrgUsers'
import { AgentAvailability } from 'types/MeetingType'

interface EditPageErrors {
  name: string
  title: string
  attendees: string
  link: string
}

interface AttendeeSelectProps {
  agentAvailabilities?: AgentAvailability[]
  updateState: (agentAvailabilities: AgentAvailability[] | undefined) => void
  disabled: boolean
  errors: EditPageErrors
  setErrors: (errors: EditPageErrors) => void
}

interface AgentAvailabilityOption {
  label: string
  value: number
  isFixed?: boolean
}

const AttendeeSelect = ({
  agentAvailabilities,
  updateState,
  disabled,
  errors,
  setErrors,
}: AttendeeSelectProps) => {
  const currentUser = useCurrentUser()
  const { filteredConnectedAgents } = useCalendarConnectedUsers()
  const agentSelectOptions = filteredConnectedAgents.map((agent: User) => ({
    label: (agent.name ? agent.name : agent.email) ?? 'Teammate',
    value: agent.id,
  }))
  const currentUserOption = {
    label:
      (currentUser.name ? currentUser.name : currentUser.email) ?? 'Teammate',
    value: currentUser.id,
    isFixed: true,
  }
  const savedAgentAvailabilities = agentSelectOptions.filter((option) =>
    agentAvailabilities?.some((agent) => agent.agentId === option.value)
  )
  const defaultAgentAvailabilities: AgentAvailabilityOption[] =
    savedAgentAvailabilities.slice()
  defaultAgentAvailabilities.unshift(currentUserOption)

  const [isGroupMeeting, setIsGroupMeeting] = useState<boolean>(
    agentAvailabilities === undefined
  )
  useEffect(() => {
    setIsGroupMeeting(!!agentAvailabilities)
  }, [agentAvailabilities])

  const getStateToSave = (options: AgentAvailabilityOption[]) =>
    options.map((option) => {
      return {
        shouldCheckAvailability: true,
        agentId: option.value,
      }
    })
  const handleSelectionChange = (options: AgentAvailabilityOption[]) => {
    if (isGroupMeeting) {
      //remove author from the saved values
      const optionsToSave = options.slice(1)
      const newAgentAvailablities = getStateToSave(optionsToSave)
      updateState(newAgentAvailablities)
    } else {
      //should never get here functionally, but for safety, if the select is altered and it's marked
      //as a one-on-one, set the availabilities to undefined
      updateState(undefined)
    }
  }

  const handleRadioChange = (val: string) => {
    setIsGroupMeeting(val === 'true')
    if (val === 'false') {
      updateState(undefined)
      setErrors({ ...errors, attendees: '' })
    } else {
      updateState(getStateToSave(savedAgentAvailabilities ?? []))
    }
  }

  return (
    <>
      <RadioGroup
        label="Event type"
        value={isGroupMeeting ? 'true' : 'false'}
        onValueChange={handleRadioChange}
      >
        <RadioItem
          value="false"
          label="One-on-one"
          description="Meetings will be scheduled based on your personal availability"
        />
        <RadioItem
          value="true"
          label="Group"
          description="Meetings will only be scheduled when chosen teammates are mutually available"
        />
      </RadioGroup>
      {isGroupMeeting && (
        <Select
          size={'x-large'}
          multi
          label="Add teammates for a group meeting"
          description="Meetings will only be scheduled when everyone is mutually available"
          options={agentSelectOptions}
          value={defaultAgentAvailabilities}
          // @ts-expect-error newValue in onChange is unknown
          onChange={handleSelectionChange}
          disabled={disabled}
          error={errors.attendees}
        />
      )}
    </>
  )
}

export default AttendeeSelect
